import React, { useCallback, useEffect, useState } from 'react';
import MobileSelect from '../components/MobileSelect.tsx';
import { lookupAPI, profileAPI } from '../services/api.ts';
import { Filesystem, Directory } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

interface DocumentItem {
    id: number;
    title: string;
    createdDate?: string;
    url?: string;
}

/** LookupTypeId for document-type options (LookupCode); labels use Name for the filter dropdown. */
const DOCUMENT_TYPE_LOOKUP_TYPE_ID = 23;

/** Lookup `Value` 2 = Salary (LookupTypeId 23). Only this type requires a password. */
const SALARY_DOCUMENT_TYPE_VALUE = 2;

const Payslips: React.FC = () => {
    const [documentTypeId, setDocumentTypeId] = useState<string>('');
    const [documentTypes, setDocumentTypes] = useState<{ value: string; label: string }[]>([]);
    const [documents, setDocuments] = useState<DocumentItem[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showPasswordModal, setShowPasswordModal] = useState(false);
    const [pendingDocument, setPendingDocument] = useState<DocumentItem | null>(null);
    const [passwordInput, setPasswordInput] = useState('');
    const [passwordError, setPasswordError] = useState<string | null>(null);
    const [documentActionLoading, setDocumentActionLoading] = useState(false);

    const openDocumentPreview = useCallback(async (item: DocumentItem, password: string) => {
        const isNative = Capacitor.isNativePlatform();
        const popup = !isNative ? window.open('about:blank', '_blank') : null;
        try {
            if (popup) popup.opener = null;
        } catch {
            // ignore
        }
        if (popup && popup.document) {
            popup.document.title = item.title || 'Document';
            popup.document.body.innerHTML = '<p style="font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif; padding: 16px;">Loading…</p>';
        }
        try {
            const blob = await profileAPI.downloadDocument(Number(documentTypeId), item.title, password);

            if (isNative) {
                const reader = new FileReader();
                const base64Data = await new Promise<string>((resolve, reject) => {
                    reader.onloadend = () => {
                        const base64String = reader.result as string;
                        const base64 = base64String.split(',')[1];
                        resolve(base64);
                    };
                    reader.onerror = reject;
                    reader.readAsDataURL(blob);
                });

                const sanitizedFileName = item.title.replace(/[^a-zA-Z0-9.-]/g, '_');
                const pdfFileName = sanitizedFileName.toLowerCase().endsWith('.pdf')
                    ? sanitizedFileName
                    : `${sanitizedFileName || `document_${item.id}`}.pdf`;
                const filePath = `document_${item.id}_${pdfFileName}`;

                await Filesystem.writeFile({
                    path: filePath,
                    data: base64Data,
                    directory: Directory.Cache,
                });

                const fileUri = await Filesystem.getUri({
                    path: filePath,
                    directory: Directory.Cache,
                });

                try {
                    await Share.share({
                        title: item.title,
                        url: fileUri.uri,
                        dialogTitle: 'Open Document',
                    });
                } catch (shareError: any) {
                    if (Capacitor.getPlatform() === 'android') {
                        window.open(fileUri.uri, '_system');
                    } else {
                        throw shareError;
                    }
                }
            } else {
                const blobUrl = URL.createObjectURL(blob);
                if (popup && !popup.closed) {
                    popup.location.href = blobUrl;
                } else {
                    const opened = window.open(blobUrl, '_blank');
                    if (!opened) {
                        window.location.href = blobUrl;
                    }
                }
                setTimeout(() => URL.revokeObjectURL(blobUrl), 10 * 60_000);
            }
        } catch (e: any) {
            if (popup && !popup.closed) popup.close();
            throw e;
        }
    }, [documentTypeId]);

    useEffect(() => {
        lookupAPI
            .getLookupByTypeId(DOCUMENT_TYPE_LOOKUP_TYPE_ID)
            .then((response: any) => {
                const dataArray = Array.isArray(response?.data)
                    ? response.data
                    : (response?.data?.data ?? []);
                const opts = (dataArray as any[])
                    .map((item) => {
                        const name = String(item.name ?? item.Name ?? '').trim();
                        const raw =
                            item.value ?? item.Value ?? item.typeId ?? item.TypeId ?? item.id ?? item.Id;
                        const value = String(raw ?? '').trim();
                        return { value, label: name || value };
                    })
                    .filter((o) => o.value);
                setDocumentTypes(opts);
                if (opts.length) {
                    const salary = opts.find((o) => Number(o.value) === SALARY_DOCUMENT_TYPE_VALUE);
                    setDocumentTypeId((prev) => prev || salary?.value || opts[0].value);
                }
            })
            .catch(() => setDocumentTypes([]));
    }, []);

    useEffect(() => {
        if (!documentTypeId) return;
        setLoading(true);
        setError(null);
        profileAPI.getDocuments(Number(documentTypeId))
            .then((rows: any[]) => {
                const merged = (rows || [])
                    .filter(Boolean)
                    .map((r: any, idx: number) => ({ id: Number(r.id ?? r.Id ?? idx), title: String(r.fileName ?? r.FileName ?? r.title ?? r.Title ?? ''), createdDate: r.createdDate ?? r.CreatedDateTime ?? r.date, url: r.url ?? r.Url }));
                setDocuments(merged);
            })
            .catch((e) => setError(e?.message || 'Failed to load documents'))
            .finally(() => setLoading(false));
    }, [documentTypeId]);

	return (
		<div className="px-4 py-4">
				<div className="flex items-center justify-between mb-3">
					<h2 className="text-lg font-semibold">Documents</h2>
					<div className="flex items-center gap-2">
						<span className="text-xs text-gray-500">Type</span>
						<div className="min-w-[7.5rem] max-w-[12rem] flex-1">
							<MobileSelect
								value={documentTypeId}
								options={documentTypes}
								onChange={setDocumentTypeId}
								placeholder="Select"
							/>
						</div>
					</div>
				</div>

            <div className="bg-white rounded border divide-y">
                {documents.map(item => (
                    <button
                        key={item.id}
                        className="w-full text-left px-3 py-3 flex items-center"
                        onClick={() => {
                            if (Number(documentTypeId) === SALARY_DOCUMENT_TYPE_VALUE) {
                                setPendingDocument(item);
                                setPasswordInput('');
                                setPasswordError(null);
                                setDocumentActionLoading(false);
                                setShowPasswordModal(true);
                            } else {
                                void openDocumentPreview(item, '');
                            }
                        }}
                    >
						<span className="w-9 h-9 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center mr-3">
							<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
								<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10M7 11h10M7 15h7"/>
							</svg>
						</span>
						<div className="flex-1">
                            <div className="font-medium">{item.title}</div>
                            {item.createdDate && (<div className="text-xs text-gray-500">{new Date(item.createdDate).toLocaleDateString()}</div>)}
						</div>
						<svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
						</svg>
                    </button>
				))}
                {loading && (
                    <div className="p-4 text-center">
                        <div className="inline-block animate-spin h-6 w-6 border-4 border-primary border-t-transparent rounded-full"></div>
                        <p className="mt-2 text-sm text-gray-600">Loading...</p>
                    </div>
                )}
                {error && <div className="p-4 text-sm text-red-600">{error}</div>}
                {!loading && !error && documents.length === 0 && (
                    <div className="p-4 text-sm text-gray-500">No documents found.</div>
                )}
			</div>

            {showPasswordModal && (
                <div className="fixed inset-0 z-50 flex flex-col justify-end items-center bg-black bg-opacity-70 pb-safe">
                    <div className="w-full bg-white rounded-t-2xl shadow-xl overflow-hidden max-h-[90vh] flex flex-col">
                        <div className="relative bg-primary py-4 px-4 shrink-0">
                            <h2 className="text-xl font-bold text-white text-center">Confirm password</h2>
                            <button
                                type="button"
                                onClick={() => {
                                    setShowPasswordModal(false);
                                    setPendingDocument(null);
                                    setPasswordInput('');
                                    setPasswordError(null);
                                    setDocumentActionLoading(false);
                                }}
                                className="absolute right-4 top-4 text-white"
                                aria-label="Close modal"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            </button>
                        </div>
                        <div className="p-4 overflow-y-auto">
                            <p className="text-sm text-gray-600 mb-3">
                                Enter your account password to preview this document.
                            </p>
                            <label htmlFor="payslip-doc-password" className="block text-sm font-medium text-gray-700 mb-2">
                                Password
                            </label>
                            <input
                                id="payslip-doc-password"
                                type="password"
                                autoComplete="current-password"
                                value={passwordInput}
                                onChange={(e) => {
                                    setPasswordInput(e.target.value);
                                    setPasswordError(null);
                                }}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-base"
                                placeholder="Password"
                            />
                            {passwordError && (
                                <p className="mt-2 text-sm text-red-600">{passwordError}</p>
                            )}
                        </div>
                        <div className="border-t border-gray-200 p-4 shrink-0">
                            <div className="grid grid-cols-1 gap-3">
                                <button
                                    type="button"
                                    disabled={documentActionLoading}
                                    onClick={async () => {
                                        const pwd = passwordInput.trim();
                                        if (!pwd) {
                                            setPasswordError('Please enter your password.');
                                            return;
                                        }
                                        const doc = pendingDocument;
                                        if (!doc) return;
                                        setDocumentActionLoading(true);
                                        setPasswordError(null);
                                        try {
                                            await openDocumentPreview(doc, pwd);
                                            setShowPasswordModal(false);
                                            setPendingDocument(null);
                                            setPasswordInput('');
                                        } catch (e: any) {
                                            setPasswordError(e?.message || 'Failed to open document');
                                        } finally {
                                            setDocumentActionLoading(false);
                                        }
                                    }}
                                    className="w-full py-3 rounded-md text-white font-medium text-center bg-primary disabled:opacity-60"
                                >
                                    {documentActionLoading ? 'Opening…' : 'View document'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowPasswordModal(false);
                                        setPendingDocument(null);
                                        setPasswordInput('');
                                        setPasswordError(null);
                                        setDocumentActionLoading(false);
                                    }}
                                    className="w-full py-3 border border-gray-300 rounded-md text-gray-700 font-medium text-center bg-white"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
		</div>
	);
};

export default Payslips;


